import { useCallback, useEffect, useState } from 'react';

export type SuTheme = 'dark' | 'light';
export const THEME_STORAGE_KEY = 'su_theme';

export function readStoredTheme(): SuTheme {
  try {
    return localStorage.getItem(THEME_STORAGE_KEY) === 'light' ? 'light' : 'dark';
  } catch {
    return 'dark';
  }
}

function writeStoredTheme(t: SuTheme) {
  try {
    localStorage.setItem(THEME_STORAGE_KEY, t);
  } catch {
    /* storage unavailable */
  }
}

// Tiny module-level store so every consumer (routes, More page, charts) shares one value without a provider.
let current: SuTheme | null = null;
const listeners = new Set<(t: SuTheme) => void>();

export function getTheme(): SuTheme {
  if (current === null) current = readStoredTheme();
  return current;
}

export function setTheme(next: SuTheme) {
  current = next;
  writeStoredTheme(next);
  listeners.forEach((l) => l(next));
}

/** Test helper: forget the in-memory value so the next read hits localStorage again. */
export function resetThemeForTests() {
  current = null;
  listeners.clear();
}

export function useTheme() {
  const [theme, setLocal] = useState<SuTheme>(getTheme);
  useEffect(() => {
    listeners.add(setLocal);
    setLocal(getTheme());
    return () => {
      listeners.delete(setLocal);
    };
  }, []);
  const toggle = useCallback(() => setTheme(getTheme() === 'dark' ? 'light' : 'dark'), []);
  return { theme, isDark: theme === 'dark', setTheme, toggle };
}

export const themeClass = (t: SuTheme) => (t === 'dark' ? 'su-theme-dark' : 'su-theme-light');

/** Chart colours per theme. Bar series colours are NOT theme dependent. */
export const chartColors = (t: SuTheme) =>
  t === 'dark'
    ? { text: '#f2f4fb', muted: '#8d93b0', grid: '#262c4a' }
    : { text: '#111827', muted: '#6b7280', grid: '#e5e7eb' };
